// ── STATE ────────────────────────────────────────────────────────────────────
const S = {
  cur: 0,
  answers: { homeType:null, homeSize:null, bill:null, billChange:null, symptoms:[], zones:[] }
};

// ── PRODUCT RECOMMENDATIONS ───────────────────────────────────────────────────
// Format:
//   can  → kendi nozzle/tüpü olan, tabancasız kullanım (dar boşluklar, profesyonel)
//   kit  → tabanca dahil, başlangıç için ideal
//
// Tier = tahmini miktar:
//   small  → 3'lü paket
//   medium → 12'li paket
//   large  → 24'lü paket
//   bulk   → 108–216'lı toplu paket
//
// Her hücreye ilgili Shopify variant ID'sini yazın.
// Boş ('') bırakılan hücre için bir üst tier denenir; hepsi boşsa text fallback gösterilir.
const PRODUCT_MAP = {
  can: {
    small:  '40900028661809',
    medium: '40900028727345',
    large:  '40900028760113',
    bulk:   '40900030005297',
  },
  kit: {
    small:  '40900028792881',
    medium: '40900028858417',
    large:  '40900028891185',
    bulk:   '40900030136369',
  },
};

// ── ANALYTICS / TRACKING ─────────────────────────────────────────────────────
const WEBHOOK_URL = 'https://n8n.datontech.com/webhook/energy-calculator-submit';
const WEBHOOK_SECRET = 'e395afb4c272901cca652df547f1a2330048665722fdfda7a82f9d45a3d8d6b3';

const T = (() => {
  let sid = sessionStorage.getItem('ec_sid');
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) ||
          (Date.now() + '-' + Math.random().toString(36).slice(2));
    sessionStorage.setItem('ec_sid', sid);
  }
  return {
    sessionId: sid,
    startedAt: new Date().toISOString(),
    startedAtMs: Date.now(),
    steps: [],                  // {id, durationMs, answer}
    currentStepId: 'q0',
    currentStepStart: Date.now(),
    sent: false,                // terminal flag — prevents duplicate sends
  };
})();

function answerFor(stepId) {
  const map = { q1:'homeType', q2:'homeSize', q3:'bill', q4:'billChange', q5:'symptoms', q6:'zones' };
  const k = map[stepId];
  return k ? S.answers[k] : null;
}

function trackNav(newId) {
  T.steps.push({
    id: T.currentStepId,
    durationMs: Date.now() - T.currentStepStart,
    answer: answerFor(T.currentStepId),
  });
  T.currentStepId = newId;
  T.currentStepStart = Date.now();
}

function buildPayload(eventType, extra = {}) {
  // Snapshot the in-progress step without mutating T.steps
  const inProgress = {
    id: T.currentStepId,
    durationMs: Date.now() - T.currentStepStart,
    answer: answerFor(T.currentStepId),
    inProgress: true,
  };
  const allSteps = [...T.steps, inProgress];
  const completedSteps = allSteps.filter(s => {
    if (!/^q\d+$/.test(s.id)) return false;
    const a = s.answer;
    return a != null && (!Array.isArray(a) || a.length > 0);
  }).length;
  return {
    sessionId: T.sessionId,
    eventType,
    startedAt: T.startedAt,
    endedAt: new Date().toISOString(),
    totalDurationMs: Date.now() - T.startedAtMs,
    completedSteps,
    lastStepId: T.currentStepId,
    steps: allSteps,
    answers: S.answers,
    ...extra,
  };
}

function sendEvent(eventType, extra = {}) {
  if (T.sent) return;
  // Skip empty abandons — user landed but never interacted
  if (eventType === 'abandon' && T.steps.length === 0 && T.currentStepId === 'q0') return;
  T.sent = true;
  const payload = buildPayload(eventType, extra);

  // fetch + keepalive survives page unload AND supports custom headers
  // (sendBeacon would also survive unload but can't set headers — incompatible with Header Auth)
  fetch(WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Webhook-Secret': WEBHOOK_SECRET },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

// Abandon detection — visibilitychange fires on tab switch / navigation / mobile background
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') sendEvent('abandon');
});
window.addEventListener('pagehide', () => sendEvent('abandon'));

// q-number → interstitial id → next q-number
// flow: 1→qi1→2→qi2→3→qi3→4→qi4→5→qi5→6→qi6→7→8
const FLOW = {
  1: 'qi1', 2: 'qi2', 3: 'qi3', 4: 'qi4'
};
const KEY_MAP = { 'home-type':'homeType','home-size':'homeSize','bill':'bill','bill-change':'billChange' };

// ── INTERSTITIAL DATA ────────────────────────────────────────────────────────
const INTER = {
  'home-type': {
    house:     { big:'2–3×', sub:'more leak zones', text:'Detached homes expose all four walls, the roof, and the foundation perimeter to outside air — giving leaks far more entry paths than attached or multi-unit buildings.', svg:'house' },
    townhouse: { big:'~60%', sub:'of leaks at exposed sides', text:'Shared walls reduce exposure, but your attic, front and back faces, and roof remain fully open. Most air-loss in townhouses concentrates at these boundaries.', svg:'townhouse' },
    condo:     { big:'Top floor', sub:'highest risk position', text:'Corner and top-floor units share exterior surfaces on multiple sides. That direct exposure matters more than the shared-wall insulation you have in your favour.', svg:'condo' },
    mobile:    { big:'#1', sub:'risk: floor perimeter', text:'Manufactured homes are particularly prone to air-sealing gaps at the floor perimeter and roof-wall junctions — areas missed in standard maintenance checks.', svg:'mobile' },
  },
  'home-size': {
    small:  { big:'2 ft²', sub:'of gaps = major loss', text:'Total gap area that size — spread invisibly across window frames, door edges, and sills — allows substantial continuous air movement around the clock.', svg:'size' },
    medium: { big:'2–3', sub:'accessible fix areas', text:'Homes in this range almost always concentrate air-loss in 2–3 accessible zones. Finding them first is the difference between a targeted fix and random product guessing.', svg:'size' },
    large:  { big:'5+', sub:'potential zones', text:'As floor area increases, so does total leak surface. Larger homes often have five or more distinct zones — start with the most accessible ones first.', svg:'size' },
    xlarge: { big:'10+', sub:'individual leak points', text:'Homes over 3,500 sq ft can have a dozen or more individual air-loss points. Systematic zone-by-zone diagnosis is the only reliable approach at this scale.', svg:'size' },
  },
  'bill': {
    low:   { big:'15%', sub:'typical sealing savings', text:'Even at lower spend, air-sealing a single zone commonly reduces heating and cooling costs by 10–15% — consistently among the highest-ROI home improvements available.', svg:'bill' },
    med:   { big:'$240', sub:'avg annual savings', text:'Homeowners in this spend range who address their primary leak zone report average annual savings of $200–$300 — often from a single weekend project.', svg:'bill' },
    high:  { big:'2+', sub:'likely leak zones', text:'Homes spending $200–$350/month typically have two or more significant air-loss areas contributing directly to that bill. Fixing the top zone makes a visible impact.', svg:'bill' },
    vhigh: { big:'30%', sub:'of spend may be avoidable', text:'At this level, air leakage can account for 25–35% of your total bill. That\'s not a heating problem — it\'s a sealing problem that can be addressed systematically.', svg:'bill' },
  },
  'bill-change': {
    yes:    { big:'↑', sub:'unexplained increase = leak signal', text:'Gradual, unexplained bill increases are the most reliable real-world indicator of new or worsening air gaps — often developing over 6–18 months before becoming obvious.', svg:'trend' },
    no:     { big:'≠', sub:'stable bill ≠ no leaks', text:'Consistent bills often mean you\'ve subconsciously adapted — lowering the thermostat, wearing more layers, or avoiding certain rooms. The leaks may still be there.', svg:'trend' },
    unsure: { big:'?', sub:'most homeowners don\'t track this', text:'Completely normal. Most people only notice energy waste when it becomes severe. The structured diagnostic approach removes the need to track historical data.', svg:'trend' },
  },
};

const INTER_MULTI = {
  symptoms: [
    null,
    { big:'1', sub:'symptom detected', text:'Even one comfort symptom can point directly to a specific zone. It\'s often a more precise signal than the bill itself — symptoms are your home\'s way of showing where it\'s struggling.' },
    { big:'2', sub:'symptoms overlap', text:'Two or more symptoms together frequently trace back to the same underlying leak zone — making the fix more targeted, not more complex. This is a useful pattern.' },
    { big:'3+', sub:'strong signal', text:'Three or more symptoms is a strong combined signal. Most homeowners at this level find at least one high-priority zone on first inspection — often in an accessible area.' },
    { big:'4+', sub:'multiple indicators', text:'Multiple symptoms across different areas suggest air-loss isn\'t isolated to one spot. A zone-by-zone check will be more effective than a single targeted fix.' },
  ],
  zones: {
    windows: { big:'#1', sub:'most accessible first fix', text:'Windows and doors are the most accessible starting point — and typically the highest-impact per dollar spent for most homeowners. Weatherstripping and frame sealing can be completed in an afternoon.' },
    attic:   { big:'ROI #1', sub:'highest-return improvement', text:'Attic air-sealing is frequently called the single highest-ROI improvement a certified energy auditor can recommend. Heat rises — stopping it at the attic boundary is the most effective interception point.' },
    garage:  { big:'Often missed', sub:'commonly overlooked zone', text:'The garage-to-living-area boundary is one of the most commonly overlooked zones. It can account for meaningful heat loss throughout winter — especially if the door seal or rim joist is unsealed.' },
    pipes:   { big:'Dozens', sub:'of individual entry points', text:'Pipe and cable penetrations are easy to overlook individually. Collectively, they can account for a surprising share of total air leakage — each small gap adds to the cumulative total.' },
    unsure:  { big:'Start here', sub:'general diagnostic approach', text:'Not knowing the exact location is common. Your result will map the most statistically likely zones for your home type, size, and symptom profile — giving you a prioritised starting point.' },
  }
};

// ── SVG VISUALS FOR INTERSTITIALS ───────────────────────────────────────────
function getInterSvg(key) {
  const svgs = {
    house: `<svg width="120" height="90" viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 50 L60 10 L110 50" stroke="rgba(50,74,254,.4)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><rect x="20" y="50" width="80" height="35" stroke="rgba(50,74,254,.3)" stroke-width="1.5"/><rect x="30" y="58" width="22" height="16" stroke="rgba(255,255,255,.3)" stroke-width="1.2"/><rect x="68" y="58" width="22" height="16" stroke="rgba(255,255,255,.3)" stroke-width="1.2"/><rect x="48" y="68" width="24" height="17" stroke="rgba(255,255,255,.2)" stroke-width="1.2"/><circle cx="41" cy="58" r="1.5" fill="rgba(50,74,254,.7)"><animate attributeName="cy" values="58;44;30" dur="1.8s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.7;0.3;0" dur="1.8s" repeatCount="indefinite"/></circle><circle cx="79" cy="58" r="1.5" fill="rgba(50,74,254,.7)"><animate attributeName="cy" values="58;44;30" dur="2.1s" begin=".4s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.7;0.3;0" dur="2.1s" begin=".4s" repeatCount="indefinite"/></circle></svg>`,
    townhouse: `<svg width="160" height="90" viewBox="0 0 160 90" fill="none"><path d="M5 50 L40 15 L75 50" stroke="rgba(255,255,255,.15)" stroke-width="1.5"/><rect x="10" y="50" width="60" height="35" stroke="rgba(255,255,255,.15)" stroke-width="1.5"/><path d="M80 50 L115 15 L150 50" stroke="rgba(50,74,254,.5)" stroke-width="2"/><rect x="85" y="50" width="60" height="35" stroke="rgba(50,74,254,.35)" stroke-width="1.5"/><circle cx="115" cy="45" r="2" fill="rgba(50,74,254,.8)"><animate attributeName="cy" values="45;30;15" dur="2s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.8;0.4;0" dur="2s" repeatCount="indefinite"/></circle></svg>`,
    condo: `<svg width="100" height="110" viewBox="0 0 100 110" fill="none"><rect x="20" y="10" width="60" height="95" stroke="rgba(255,255,255,.15)" stroke-width="1.5"/><rect x="20" y="10" width="60" height="28" fill="rgba(50,74,254,.1)" stroke="rgba(50,74,254,.5)" stroke-width="1.5"/><line x1="20" y1="38" x2="80" y2="38" stroke="rgba(255,255,255,.1)" stroke-width="1"/><line x1="20" y1="66" x2="80" y2="66" stroke="rgba(255,255,255,.1)" stroke-width="1"/><circle cx="50" cy="24" r="2.5" fill="rgba(50,74,254,.9)"><animate attributeName="cy" values="24;14;4" dur="1.6s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.9;0.4;0" dur="1.6s" repeatCount="indefinite"/></circle></svg>`,
    mobile: `<svg width="140" height="70" viewBox="0 0 140 70" fill="none"><path d="M10 30 L20 15 L120 15 L130 30" stroke="rgba(50,74,254,.4)" stroke-width="1.5"/><rect x="10" y="30" width="120" height="32" stroke="rgba(50,74,254,.35)" stroke-width="1.5"/><line x1="10" y1="62" x2="130" y2="62" stroke="rgba(50,74,254,.5)" stroke-width="2.5" stroke-linecap="round"/><path d="M20 62 L18 70 M50 62 L48 70 M80 62 L78 70 M110 62 L108 70" stroke="rgba(50,74,254,.4)" stroke-width="1.5" stroke-linecap="round"/></svg>`,
    size: `<svg width="120" height="100" viewBox="0 0 120 100" fill="none"><rect x="5" y="25" width="50" height="70" stroke="rgba(255,255,255,.15)" stroke-width="1.5" stroke-dasharray="3 2"/><rect x="5" y="5" width="110" height="90" stroke="rgba(50,74,254,.4)" stroke-width="1.5"/><line x1="5" y1="35" x2="115" y2="35" stroke="rgba(50,74,254,.2)" stroke-width="1" stroke-dasharray="4 3"/><line x1="5" y1="65" x2="115" y2="65" stroke="rgba(50,74,254,.2)" stroke-width="1" stroke-dasharray="4 3"/><line x1="60" y1="5" x2="60" y2="95" stroke="rgba(50,74,254,.2)" stroke-width="1" stroke-dasharray="4 3"/></svg>`,
    bill: `<svg width="120" height="90" viewBox="0 0 120 90" fill="none"><line x1="10" y1="80" x2="110" y2="80" stroke="rgba(255,255,255,.15)" stroke-width="1"/><line x1="10" y1="80" x2="10" y2="10" stroke="rgba(255,255,255,.15)" stroke-width="1"/><rect x="20" y="55" width="18" height="25" fill="rgba(255,255,255,.1)" stroke="rgba(255,255,255,.2)" stroke-width="1"/><rect x="48" y="42" width="18" height="38" fill="rgba(50,74,254,.15)" stroke="rgba(50,74,254,.3)" stroke-width="1"/><rect x="76" y="22" width="18" height="58" fill="rgba(50,74,254,.3)" stroke="rgba(50,74,254,.5)" stroke-width="1.5"/><path d="M29 52 L57 39 L85 19" stroke="rgba(50,74,254,.6)" stroke-width="1.5" stroke-dasharray="3 2"/></svg>`,
    trend: `<svg width="130" height="80" viewBox="0 0 130 80" fill="none"><line x1="10" y1="70" x2="120" y2="70" stroke="rgba(255,255,255,.1)" stroke-width="1"/><line x1="10" y1="70" x2="10" y2="10" stroke="rgba(255,255,255,.1)" stroke-width="1"/><path d="M15 60 L35 55 L55 52 L75 45 L95 35 L115 20" stroke="rgba(50,74,254,.7)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="115" cy="20" r="4" fill="rgba(50,74,254,.9)"/><path d="M15 40 L115 40" stroke="rgba(255,255,255,.08)" stroke-width="1" stroke-dasharray="4 3"/></svg>`,
  };
  return svgs[key] || svgs['house'];
}

// ── RIPPLE ───────────────────────────────────────────────────────────────────
function spawnRipple(btn, e) {
  const r = document.createElement('span');
  r.className = 'ripple-el';
  const rect = btn.getBoundingClientRect();
  r.style.top  = (e.clientY - rect.top)  + 'px';
  r.style.left = (e.clientX - rect.left) + 'px';
  btn.appendChild(r);
  r.addEventListener('animationend', () => r.remove());
}

// ── NAVIGATION ───────────────────────────────────────────────────────────────
let interTimer = null;

function goQ(n) {
  const prevId = idForIndex(S.cur);
  const nextId = idForIndex(n);
  trackNav(nextId);

  const dir = n > S.cur ? 'right' : 'left';
  document.getElementById(prevId).classList.remove('active','enter-right','enter-left');
  S.cur = n;
  const screen = document.getElementById(nextId);
  screen.classList.add('active', 'enter-' + dir);
  setTimeout(() => screen.classList.remove('enter-right','enter-left'), 350);

  const pct = Math.round((visibleStep(n) / 7) * 100);
  const bar = document.getElementById('quiz-progress');
  bar.style.width = pct + '%';

  const counter = screen.querySelector('.quiz-counter');
  if (counter) { counter.classList.remove('updated'); void counter.offsetWidth; counter.classList.add('updated'); }

  if (n === 7) updateRiskPreview();
  if (n === 8) renderResults();

  document.getElementById('quiz-section').scrollIntoView({ behavior:'smooth', block:'start' });
}

// Map logical index to element id (qi1..qi6 are interstitials between q1-q6)
function idForIndex(n) {
  const map = { 0:'q0',1:'q1','qi1':'qi1',2:'q2','qi2':'qi2',3:'q3','qi3':'qi3',4:'q4','qi4':'qi4',5:'q5','qi5':'qi5',6:'q6','qi6':'qi6',7:'q7',8:'q8' };
  return map[n] || 'q' + n;
}

function visibleStep(n) {
  if (typeof n === 'string') return parseInt(n.replace('qi','')) - 0.5;
  return n;
}

function scrollToQuiz() { document.getElementById('quiz-section').scrollIntoView({ behavior:'smooth' }); }

// ── INTERSTITIAL LAUNCH ──────────────────────────────────────────────────────
function launchInter(qiId, nextQ, data) {
  if (!data) { goQ(nextQ); return; }
  trackNav(qiId);

  document.getElementById(qiId + '-visual').innerHTML = getInterSvg(data.svg || 'house');
  document.getElementById(qiId + '-big').textContent   = data.big;
  document.getElementById(qiId + '-bigsub').textContent = data.sub;
  document.getElementById(qiId + '-text').innerHTML    = data.text;

  // Show screen
  const prev = document.getElementById(idForIndex(S.cur));
  prev.classList.remove('active','enter-right','enter-left');
  S.cur = qiId;
  const screen = document.getElementById(qiId);
  screen.classList.add('active','enter-right');
  setTimeout(() => screen.classList.remove('enter-right'), 350);

  document.getElementById('quiz-section').scrollIntoView({ behavior:'smooth', block:'start' });

  // Timer bar
  const fill = document.getElementById(qiId + '-fill');
  fill.style.transition = 'none';
  fill.style.width = '0%';
  void fill.offsetWidth;
  fill.style.transition = 'width 1.4s linear';
  fill.style.width = '100%';

  if (interTimer) clearTimeout(interTimer);
  interTimer = setTimeout(() => goQ(nextQ), 1500);
}

function skipInter(nextQ) {
  if (interTimer) { clearTimeout(interTimer); interTimer = null; }
  goQ(nextQ);
}

// ── SINGLE SELECT ────────────────────────────────────────────────────────────
function selectSingle(btn, group, nextQ, e) {
  if (e) spawnRipple(btn, e);
  document.querySelectorAll('[data-group="' + group + '"]').forEach(b => b.classList.remove('selected'));
  btn.classList.add('selected');
  const key = KEY_MAP[group] || group;
  S.answers[key] = btn.dataset.val;
  const nxtBtn = document.getElementById('nxt' + S.cur);
  if (nxtBtn) nxtBtn.disabled = false;

  // Determine interstitial id
  const qiMap = { 1:'qi1', 2:'qi2', 3:'qi3', 4:'qi4' };
  const qiId = qiMap[S.cur];
  if (qiId) {
    const groupData = INTER[group];
    const data = groupData ? groupData[btn.dataset.val] : null;
    setTimeout(() => launchInter(qiId, nextQ, data), 320);
  } else {
    setTimeout(() => goQ(nextQ), 320);
  }
}

// ── MULTI SELECT ─────────────────────────────────────────────────────────────
function toggleMulti(btn, group, nextQ, e) {
  if (e) spawnRipple(btn, e);
  btn.classList.toggle('selected');
  const parent = btn.closest('.quiz-options');
  const selected = [...parent.querySelectorAll('.q-opt.selected')].map(b => b.dataset.val);
  S.answers[group] = selected;
  const nxtBtn = document.getElementById('nxt' + S.cur);
  if (nxtBtn) nxtBtn.disabled = selected.length === 0;
}

function continueWithInter(qiId, nextQ) {
  const group = qiId === 'qi5' ? 'symptoms' : 'zones';
  const selected = S.answers[group] || [];
  let data = null;

  if (group === 'symptoms') {
    const count = Math.min(selected.filter(x => x !== 'none').length, INTER_MULTI.symptoms.length - 1);
    data = count > 0 ? INTER_MULTI.symptoms[count] : null;
    if (data) data = { ...data, svg: 'size' };
  } else {
    for (const z of ['attic','windows','garage','pipes','unsure']) {
      if (selected.includes(z)) { data = { ...INTER_MULTI.zones[z], svg: 'house' }; break; }
    }
  }

  const nxtBtn = document.getElementById('nxt' + (group === 'symptoms' ? 5 : 6));
  if (nxtBtn) nxtBtn.disabled = true;
  launchInter(qiId, nextQ, data);
}

// ── SCORING ──────────────────────────────────────────────────────────────────
function calcScore() {
  let s = 0;
  s += ({house:8,townhouse:5,condo:2,mobile:10}[S.answers.homeType]||5);
  s += ({small:3,medium:6,large:9,xlarge:12}[S.answers.homeSize]||6);
  s += ({low:2,med:5,high:9,vhigh:14}[S.answers.bill]||5);
  s += S.answers.billChange==='yes'?10:S.answers.billChange==='unsure'?4:0;
  s += Math.min(S.answers.symptoms.filter(x=>x!=='none').length*5,20);
  s += Math.min(S.answers.zones.filter(x=>x!=='unsure').length*4,16);
  return Math.min(s,100);
}

function getRisk(score) {
  if (score<25) return {level:'low',label:'Low Risk',color:'#4ade80',sub:'Your home appears relatively well-sealed. Periodic maintenance checks on weatherstripping and door seals can keep it that way.'};
  if (score<50) return {level:'moderate',label:'Moderate Risk',color:'#fbbf24',sub:'You likely have one or more accessible air-leak areas contributing to higher bills or comfort inconsistencies. A targeted fix can make a real difference.'};
  if (score<75) return {level:'high',label:'High Risk',color:'#f87171',sub:'Multiple signs point to avoidable air leakage in accessible areas. The most common leaks are fixable without major renovation.'};
  return {level:'vhigh',label:'Very High Risk',color:'#ef4444',sub:'Multiple symptoms and likely leak zones suggest meaningful air-loss impacting your bills and comfort. Start with the highest-priority area first.'};
}

function updateRiskPreview() {
  document.getElementById('risk-preview-val').textContent = getRisk(calcScore()).label;
}

function checkEmail() {
  const val = document.getElementById('email-input').value;
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && document.getElementById('consent').checked;
  document.getElementById('btn-submit').disabled = !ok;
}

function submitQuiz() {
  const email = document.getElementById('email-input').value;
  const score = calcScore();
  const risk  = getRisk(score);
  sendEvent('submit', { email, score, risk: risk.label });
  goQ(8);
}

// ── RENDER RESULTS ───────────────────────────────────────────────────────────
function renderResults() {
  const score = calcScore();
  const risk = getRisk(score);

  const lbl = document.getElementById('result-risk-label');
  lbl.textContent = risk.label;
  lbl.className = 'result-risk ' + risk.level;
  document.getElementById('result-sub-text').textContent = risk.sub;

  const fg = document.getElementById('sc-fg');
  fg.style.stroke = risk.color;
  const C = 327;
  setTimeout(() => {
    fg.style.strokeDashoffset = C - (score/100)*C;
    let t=0,start;
    const step = ts => { if(!start)start=ts; t=Math.min(Math.round(((ts-start)/1400)*score),score); document.getElementById('score-n').textContent=t; if(t<score)requestAnimationFrame(step); };
    requestAnimationFrame(step);
  }, 80);

  // Highlight zone diagram
  const selZones = S.answers.zones;
  const notSure = selZones.includes('unsure') || selZones.length===0;
  ['attic','windows','garage','pipes'].forEach(z => {
    const el = document.getElementById('hd-'+z);
    if (el) {
      if (notSure || selZones.includes(z)) el.classList.add('lit');
      else el.classList.remove('lit');
    }
  });

  // Solutions — product cards if EC_PRODUCTS is configured, text fallback otherwise
  const solContainer = document.getElementById('result-solutions');
  const hasProducts = renderProductCards(solContainer);
  if (!hasProducts) {
    solContainer.className = 'solutions-list';
    const sols = buildSols(notSure);
    solContainer.innerHTML = sols.map((s,i)=>`
      <div class="sol-row ${i===0?'primary':''}">
        <div class="sol-row-left">
          ${i===0?'<div class="sol-badge">Start here</div>':''}
          <h4>${s.title}</h4><p>${s.desc}</p>
        </div>
        <a href="#" class="btn-sol ${i>0?'ghost':''}">${s.cta}</a>
      </div>`).join('');
  }
}

function pickVariants() {
  const { homeSize, zones } = S.answers;
  const realZones = (zones || []).filter(z => z !== 'unsure');
  const notSure = !realZones.length;

  // can → sadece pencere/boru gibi dar noktalarda, tabancasız kullanım
  // kit → geniş alan (tavan arası, garaj) veya emin değilse başlangıç için
  const wideAreaZones = ['attic', 'garage'];
  const needsGun = notSure || realZones.some(z => wideAreaZones.includes(z));
  const type = needsGun ? 'kit' : 'can';

  // Miktar tieri: ev büyüklüğü puanı + zone sayısı puanı
  const sizeScore   = { small:1, medium:2, large:3, xlarge:4 }[homeSize] || 2;
  const zoneScore   = notSure ? 0 : Math.min(realZones.length, 4);
  const total       = sizeScore + zoneScore;
  const primaryTier = total <= 2 ? 'small' : total <= 4 ? 'medium' : total <= 6 ? 'large' : 'bulk';

  const tiers = ['small', 'medium', 'large', 'bulk'];

  // Bir tierdan variant ID bul; boşsa bir üstünü dene
  function resolveId(t) {
    const idx = tiers.indexOf(t);
    for (let i = idx; i < tiers.length; i++) {
      const id = PRODUCT_MAP[type][tiers[i]];
      if (id) return id;
    }
    return null;
  }

  const primaryId   = resolveId(primaryTier);
  const nextTier    = tiers[Math.min(tiers.indexOf(primaryTier) + 1, tiers.length - 1)];
  const secondaryId = primaryTier !== nextTier ? resolveId(nextTier) : null;

  return [primaryId, secondaryId].filter(Boolean);
}

function renderProductCards(container) {
  const catalog = window.EC_PRODUCTS || {};
  const ids = pickVariants();
  const items = ids.map(id => catalog[id]).filter(Boolean);
  if (!items.length) return false;
  container.className = 'ec-prod-grid';
  container.innerHTML = items.map((p, i) => `
    <a href="${p.url}" class="ec-prod-card${i === 0 ? ' primary' : ''}">
      <div class="ec-prod-img">
        ${p.image
          ? `<img src="${p.image}" alt="${p.productTitle}" loading="lazy">`
          : '<span class="ec-prod-img-placeholder">No image</span>'}
      </div>
      <div class="ec-prod-body">
        ${i === 0 ? '<span class="ec-prod-badge">Start here</span>' : ''}
        <div class="ec-prod-title">${p.productTitle}</div>
        ${p.variantTitle && p.variantTitle !== 'Default Title'
          ? `<div class="ec-prod-variant">${p.variantTitle}</div>` : ''}
        <div class="ec-prod-price">${p.price || ''}</div>
      </div>
      <div class="ec-prod-cta">
        Shop Now
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </div>
    </a>`).join('');
  return true;
}

function buildSols(notSure) {
  if (notSure) return [
    {title:'General Air-Sealing Starter Kit',desc:'Covers the most common accessible leak paths in any home — a practical starting point.',cta:'Shop Starter Kit'},
    {title:'Window & Door Sealing',desc:'Gap and crack sealing for frames, sills, and weatherstripping.',cta:'See Window Fixes'},
    {title:'Attic & Upper-Level Sealing',desc:'Expanding foam and insulation path for attic access and top-plate leaks.',cta:'Shop Attic Solutions'},
  ];
  const z = S.answers.zones, paths=[];
  if (z.includes('windows')) paths.push({title:'Window & Door Sealing',desc:'Gap and crack sealing for frames, door edges, and weatherstripping — most accessible first fix.',cta:'See Window & Door Fixes'});
  if (z.includes('attic'))   paths.push({title:'Attic & Ceiling Air-Sealing',desc:'Expanding spray foam and insulation for attic hatches and top-plate leaks.',cta:'Shop Attic Solutions'});
  if (z.includes('garage'))  paths.push({title:'Garage & Basement Sealing',desc:'Targeted air-sealing for rim joists, crawl space edges, and door transitions.',cta:'See Lower-Level Fixes'});
  if (z.includes('pipes'))   paths.push({title:'Penetration & Gap Sealing',desc:'Focused solutions for utility entry points, cable gaps, and pipe runs.',cta:'Seal Penetrations'});
  if (!paths.length) paths.push({title:'General Fix Kit',desc:'A practical starting point covering the most common accessible leak paths.',cta:'Start with a Fix Kit'});
  return paths;
}

function toggleFaq(btn) { btn.closest('.faq-item').classList.toggle('open'); }

// ── IP LOCATION + CLIMATE BANNER ─────────────────────────────────────────────
const VERY_COLD = new Set(['AK','MN','ND','SD','MT','WI','MI','ME','VT','NH','WY']);
const COLD      = new Set(['ID','CO','NE','IA','IL','IN','OH','PA','NY','MA','CT','RI','NJ','UT','WA','OR','NM']);
const WARM      = new Set(['FL','TX','AZ','LA','MS','AL','GA','SC','HI','CA']);

function getClimateZone(sc) {
  if (VERY_COLD.has(sc)) return 'very_cold';
  if (COLD.has(sc))      return 'cold';
  if (WARM.has(sc))      return 'warm';
  return 'mixed';
}

const CLIMATE_COPY = {
  very_cold: {
    title: (city, state) => `${city} area homes can lose up to 35% of heat through uninsulated gaps`,
    sub:   (city, state) => `${state}'s severe winters mean poor air sealing costs significantly more here than in milder climates — every unsealed gap compounds each heating month.`,
    stat:  '35%',
    label: 'avg heat loss through uninsulated areas in cold-climate homes'
  },
  cold: {
    title: (city, state) => `${city} homes face 4+ months of heavy heating season`,
    sub:   (city, state) => `Year-round temperature swings in ${state} mean air sealing pays off in both heating and cooling seasons — typical savings after sealing key zones: $250–$400/year.`,
    stat:  '$300+',
    label: 'typical annual savings after sealing key zones'
  },
  mixed: {
    title: (city, state) => `${city} homes experience both heating and cooling exposure`,
    sub:   (city, state) => `Mixed climates like ${state} see air-sealing savings across all seasons — both energy bills and humidity control improve after treatment.`,
    stat:  '20–25%',
    label: 'combined heating + cooling savings in your climate zone'
  },
  warm: {
    title: (city, state) => `${city} homes: unsealed gaps let heat and humidity in`,
    sub:   (city, state) => `Even in ${state}'s warm climate, air gaps increase cooling costs and allow humidity infiltration — leading to mold risk and HVAC overwork.`,
    stat:  '15–20%',
    label: 'cooling savings potential after sealing key zones'
  }
};

function renderLocationBanner(city, state, stateCode) {
  const zone = getClimateZone(stateCode);
  const copy = CLIMATE_COPY[zone];
  document.getElementById('loc-title').textContent      = copy.title(city, state);
  document.getElementById('loc-sub').textContent        = copy.sub(city, state);
  document.getElementById('loc-stat-num').textContent   = copy.stat;
  document.getElementById('loc-stat-label').textContent = copy.label;
  document.getElementById('location-banner-wrap').style.display = 'block';
}

(function fetchLocation() {
  fetch('https://ipapi.co/json/')
    .then(r => r.json())
    .then(d => {
      const cc = d.country_code || '';
      if (cc !== 'US' && cc !== '') return; // only show for US or unknown
      const city  = d.city || '';
      const state = d.region || '';
      const sc    = d.region_code || '';
      if (city && state) renderLocationBanner(city, state, sc);
    })
    .catch(() => {}); // silent fail — banner stays hidden
})();